import { useState, useRef, useEffect } from 'react';
import { Download, MousePointer2, Square, Circle as CircleIcon, ArrowRight, Type, Highlighter, Eraser, Undo2 } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Input';
import { Slider } from '@/components/ui/Slider';
import { Dropzone } from '@/components/ui/Dropzone';
import { useApp } from '@/store/app';
import { downloadCanvas, loadImage, readFileAsDataURL } from '@/lib/utils';
import { toast } from 'sonner';
import { cn } from '@/lib/cn';

type Tool = 'select' | 'rect' | 'circle' | 'arrow' | 'text' | 'highlight';

interface Shape {
  tool: Tool;
  x: number; // 0..1
  y: number;
  x2: number;
  y2: number;
  color: string;
  width: number;
  text?: string;
}

const TOOLS: { id: Tool; icon: typeof Square; label: string }[] = [
  { id: 'select', icon: MousePointer2, label: 'Select' },
  { id: 'rect', icon: Square, label: 'Rectangle' },
  { id: 'circle', icon: CircleIcon, label: 'Ellipse' },
  { id: 'arrow', icon: ArrowRight, label: 'Arrow' },
  { id: 'highlight', icon: Highlighter, label: 'Highlight' },
  { id: 'text', icon: Type, label: 'Text' },
];

export function ImageAnnotator() {
  const { pendingFile, setPendingFile } = useApp();
  const [file, setFile] = useState<File | null>(null);
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [shapes, setShapes] = useState<Shape[]>([]);
  const [tool, setTool] = useState<Tool>('rect');
  const [color, setColor] = useState('#ef4444');
  const [width, setWidth] = useState(4);
  const [draft, setDraft] = useState<Shape | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (pendingFile && pendingFile.type.startsWith('image/')) {
      handleFile([pendingFile]);
      setPendingFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFile(files: File[]) {
    const f = files[0];
    if (!f) return;
    setFile(f);
    setShapes([]);
    const url = await readFileAsDataURL(f);
    const im = await loadImage(url);
    setImg(im);
  }

  // Re-render
  useEffect(() => {
    if (!img || !canvasRef.current) return;
    const c = canvasRef.current;
    c.width = img.width;
    c.height = img.height;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(img, 0, 0);
    const all = draft ? [...shapes, draft] : shapes;
    for (const s of all) drawShape(ctx, s, c.width, c.height);
  }, [img, shapes, draft]);

  function pos(e: React.PointerEvent<HTMLDivElement>) {
    const rect = wrapRef.current!.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    };
  }

  function down(e: React.PointerEvent<HTMLDivElement>) {
    if (tool === 'select') return;
    const p = pos(e);
    if (tool === 'text') {
      const text = window.prompt('Text to add:');
      if (!text) return;
      setShapes((s) => [...s, { tool, x: p.x, y: p.y, x2: p.x, y2: p.y, color, width, text }]);
      return;
    }
    setDraft({ tool, x: p.x, y: p.y, x2: p.x, y2: p.y, color, width });
  }
  function move(e: React.PointerEvent<HTMLDivElement>) {
    if (!draft) return;
    const p = pos(e);
    setDraft({ ...draft, x2: p.x, y2: p.y });
  }
  function up() {
    if (!draft) return;
    setShapes((arr) => [...arr, draft]);
    setDraft(null);
  }

  function exportPng() {
    if (!canvasRef.current || !file) return;
    downloadCanvas(canvasRef.current, file.name.replace(/\.\w+$/, '') + '-annotated.png');
    toast.success('Saved');
  }

  return (
    <div>
      <ToolHeader
        toolId="image-annotator"
        title="Image annotator"
        description="Mark up screenshots with rectangles, ellipses, arrows, highlights, and text."
      />

      {!img ? (
        <Dropzone accept="image/*" onFiles={handleFile} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
          <div className="space-y-3">
            <Card className="!p-2">
              <div className="grid grid-cols-3 gap-1">
                {TOOLS.map((t) => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setTool(t.id)}
                      className={cn(
                        'flex flex-col items-center justify-center gap-1 py-2 rounded-md text-2xs transition',
                        tool === t.id
                          ? 'bg-accent/10 text-accent border border-accent/30'
                          : 'hover:bg-surface-hover text-fg-muted',
                      )}
                    >
                      <Icon size={14} />
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </Card>

            <Card className="space-y-3">
              <Field label="Color">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {['#ef4444', '#f59e0b', '#eab308', '#10b981', '#06b6d4', '#6366f1', '#a855f7', '#ec4899', '#000000', '#ffffff'].map((c) => (
                    <button
                      key={c}
                      onClick={() => setColor(c)}
                      className={cn(
                        'size-6 rounded border transition',
                        color === c ? 'border-fg ring-2 ring-accent/30' : 'border-border',
                      )}
                      style={{ background: c }}
                    />
                  ))}
                </div>
              </Field>
              <Field label="Stroke width" hint={`${width}px`}>
                <Slider value={width} onChange={setWidth} min={1} max={20} showValue={false} />
              </Field>
            </Card>

            <Card className="space-y-2">
              <Button
                size="sm"
                variant="ghost"
                className="w-full"
                onClick={() => setShapes((s) => s.slice(0, -1))}
                disabled={!shapes.length}
              >
                <Undo2 size={11} />
                Undo
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="w-full"
                onClick={() => setShapes([])}
                disabled={!shapes.length}
              >
                <Eraser size={11} />
                Clear all
              </Button>
              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border">
                <Button size="sm" variant="secondary" onClick={() => { setImg(null); setFile(null); setShapes([]); }}>
                  New
                </Button>
                <Button size="sm" variant="primary" onClick={exportPng}>
                  <Download size={11} />
                  Save
                </Button>
              </div>
            </Card>
          </div>

          <Card className="!p-3 overflow-hidden">
            <div
              ref={wrapRef}
              onPointerDown={down}
              onPointerMove={move}
              onPointerUp={up}
              onPointerLeave={up}
              className={cn(
                'relative inline-block max-w-full mx-auto rounded shadow-card touch-none select-none',
                tool === 'select' ? 'cursor-default' : 'cursor-crosshair',
              )}
            >
              <canvas ref={canvasRef} className="max-w-full h-auto block" />
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

function drawShape(ctx: CanvasRenderingContext2D, s: Shape, W: number, H: number) {
  ctx.strokeStyle = s.color;
  ctx.fillStyle = s.color;
  ctx.lineWidth = s.width;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  const x = s.x * W;
  const y = s.y * H;
  const x2 = s.x2 * W;
  const y2 = s.y2 * H;

  if (s.tool === 'rect') {
    ctx.strokeRect(Math.min(x, x2), Math.min(y, y2), Math.abs(x - x2), Math.abs(y - y2));
  } else if (s.tool === 'circle') {
    const cx = (x + x2) / 2;
    const cy = (y + y2) / 2;
    const rx = Math.abs(x - x2) / 2;
    const ry = Math.abs(y - y2) / 2;
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    ctx.stroke();
  } else if (s.tool === 'arrow') {
    drawArrow(ctx, x, y, x2, y2, s.width);
  } else if (s.tool === 'highlight') {
    const prev = ctx.globalAlpha;
    ctx.globalAlpha = 0.35;
    ctx.fillRect(Math.min(x, x2), Math.min(y, y2), Math.abs(x - x2), Math.abs(y - y2));
    ctx.globalAlpha = prev;
  } else if (s.tool === 'text') {
    ctx.font = `${600} ${Math.max(20, s.width * 5)}px Inter, sans-serif`;
    ctx.fillText(s.text ?? '', x, y);
  }
}

function drawArrow(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number, w: number) {
  const head = Math.max(8, w * 4);
  const angle = Math.atan2(y2 - y1, x2 - x1);
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - head * Math.cos(angle - Math.PI / 7), y2 - head * Math.sin(angle - Math.PI / 7));
  ctx.lineTo(x2 - head * Math.cos(angle + Math.PI / 7), y2 - head * Math.sin(angle + Math.PI / 7));
  ctx.closePath();
  ctx.fill();
}

export default ImageAnnotator;
